import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  teachers: [],
  invitations: [],
  accessRequests: [],
  stats: {
    teachers: { total: 0, active: 0, inactive: 0 },
    invitations: { pending: 0, accepted: 0, expired: 0 },
    accessRequests: { pending: 0, approved: 0, rejected: 0, total: 0 }
  },
  pagination: {
    teachers: { total: 0, page: 1, limit: 20, pages: 0 },
    invitations: { total: 0, page: 1, limit: 20, pages: 0 },
    accessRequests: { total: 0, page: 1, limit: 20, pages: 0 }
  },
  loading: false,
  accessRequestsLoading: false,
  error: null,
  // Constants from backend
  teachingLevelClasses: {
    'Pre-Primary': [],
    'Primary': [1, 2, 3, 4, 5],
    'Secondary': [6, 7, 8, 9, 10],
    'Higher Secondary': [11, 12]
  },
  subjects: [
    'Mathematics',
    'English',
    'Science',
    'Hindi',
    'Social Studies',
    'Computer Science',
    'Physical Education',
    'Art'
  ]
};

const teacherSlice = createSlice({
  name: 'teacher',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Teachers
    setTeachers: (state, action) => {
      state.teachers = action.payload.teachers;
      state.pagination.teachers = action.payload.pagination;
      state.loading = false;
    },
    addTeacher: (state, action) => {
      state.teachers.unshift(action.payload);
    },
    updateTeacherInList: (state, action) => {
      const index = state.teachers.findIndex(t => t._id === action.payload._id);
      if (index !== -1) {
        state.teachers[index] = action.payload;
      }
    },
    removeTeacher: (state, action) => {
      state.teachers = state.teachers.filter(t => t._id !== action.payload);
    },
    // Invitations
    setInvitations: (state, action) => {
      state.invitations = action.payload.invitations;
      state.pagination.invitations = action.payload.pagination;
      state.loading = false;
    },
    addInvitation: (state, action) => {
      state.invitations.unshift(action.payload);
      state.stats.invitations.pending += 1;
    },
    updateInvitation: (state, action) => {
      const index = state.invitations.findIndex(i => i._id === action.payload._id);
      if (index !== -1) {
        const oldStatus = state.invitations[index].status;
        const newStatus = action.payload.status;
        state.invitations[index] = action.payload;

        // Update stats based on status change
        if (oldStatus !== newStatus) {
          if (oldStatus === 'pending') state.stats.invitations.pending -= 1;
          if (oldStatus === 'accepted') state.stats.invitations.accepted -= 1;
          if (oldStatus === 'expired') state.stats.invitations.expired -= 1;

          if (newStatus === 'pending') state.stats.invitations.pending += 1;
          if (newStatus === 'accepted') state.stats.invitations.accepted += 1;
          if (newStatus === 'expired') state.stats.invitations.expired += 1;
        }
      }
    },
    // Access Requests
    setAccessRequestsLoading: (state, action) => {
      state.accessRequestsLoading = action.payload;
    },
    setAccessRequests: (state, action) => {
      state.accessRequests = action.payload.accessRequests;
      state.pagination.accessRequests = action.payload.pagination;
      state.accessRequestsLoading = false;
    },
    updateAccessRequest: (state, action) => {
      const index = state.accessRequests.findIndex(r => r._id === action.payload._id);
      if (index !== -1) {
        const oldStatus = state.accessRequests[index].status;
        const newStatus = action.payload.status;
        state.accessRequests[index] = action.payload;

        // Update stats based on status change
        if (oldStatus !== newStatus) {
          if (oldStatus === 'pending') state.stats.accessRequests.pending -= 1;
          if (oldStatus === 'approved') state.stats.accessRequests.approved -= 1;
          if (oldStatus === 'rejected') state.stats.accessRequests.rejected -= 1;

          if (newStatus === 'pending') state.stats.accessRequests.pending += 1;
          if (newStatus === 'approved') state.stats.accessRequests.approved += 1;
          if (newStatus === 'rejected') state.stats.accessRequests.rejected += 1;
        }
      }
    },
    removeAccessRequest: (state, action) => {
      state.accessRequests = state.accessRequests.filter(r => r._id !== action.payload);
    },
    setAccessRequestStats: (state, action) => {
      state.stats.accessRequests = action.payload;
    },
    // Stats
    setStats: (state, action) => {
      state.stats = { ...state.stats, ...action.payload };
      state.loading = false;
    },
    // Constants
    setConstants: (state, action) => {
      if (action.payload.teachingLevelClasses) {
        state.teachingLevelClasses = action.payload.teachingLevelClasses;
      }
      if (action.payload.subjects) {
        state.subjects = action.payload.subjects;
      }
    },
    // Reset state
    resetTeacherState: () => initialState
  }
});

export const {
  setLoading,
  setError,
  clearError,
  setTeachers,
  addTeacher,
  updateTeacherInList,
  removeTeacher,
  setInvitations,
  addInvitation,
  updateInvitation,
  setAccessRequestsLoading,
  setAccessRequests,
  updateAccessRequest,
  removeAccessRequest,
  setAccessRequestStats,
  setStats,
  setConstants,
  resetTeacherState
} = teacherSlice.actions;

export default teacherSlice.reducer;
